/**
 * Calibration — fitting per-performer Ranges from sampled raw measures.
 *
 * PURE: no DOM, no storage, no camera. The wizard (ui/calibrate.ts) collects
 * samples and owns localStorage; this module owns every decision about what
 * the samples mean. That split is what makes "does calibration produce sane
 * ranges" a unit test instead of a device session.
 *
 * Implements gesture-vocabulary.md §Calibration:
 *   steps 1-3 fit aperture / spread / height ranges,
 *   step 4 captures the RESTING height so the bottom deadzone lands where this
 *   performer's arms actually rest — a position, not a pose.
 */

import { DEFAULT_RANGES, type Ranges } from './features';

export interface CalibrationSamples {
  /** Raw thumb-index ratios from "open and close your pinch". */
  aperture: number[];
  /** Raw index-pinky ratios from "spread wide, then relax". */
  spread: number[];
  /** Raw heights from "reach high, then low". */
  height: number[];
  /** Raw heights from "drop your hands". */
  rest: number[];
}

export interface FitResult {
  ranges: Ranges;
  /** Human-readable per-axis fallbacks — shown to the performer verbatim. */
  issues: string[];
}

/**
 * Robust bounds: percentiles, not min/max — a single tracker glitch in three
 * seconds of sampling must not own the range for the whole session.
 */
export function percentile(sorted: readonly number[], p: number): number {
  if (sorted.length === 0) return NaN;
  const idx = (sorted.length - 1) * p;
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (idx - lo);
}

const median = (xs: readonly number[]): number => {
  const s = [...xs].sort((a, b) => a - b);
  return percentile(s, 0.5);
};

/**
 * The smallest span an axis may calibrate to. Below this, the step barely
 * moved — a fitted range would amplify noise into full-scale swings, so the
 * default is kept and the performer is told so.
 */
const MIN_SPAN: Record<keyof Ranges, number> = {
  aperture: 0.25,
  spread: 0.3,
  height: 0.2,
};

/** Keep the resting arm strictly inside the dead zone, with room to spare. */
const REST_MARGIN = 0.04;

function fitAxis(
  name: keyof Ranges,
  samples: readonly number[],
  issues: string[],
): [number, number] {
  const fallback = DEFAULT_RANGES[name];
  if (samples.length < 10) {
    issues.push(`${name}: too few samples — kept the default range`);
    return [...fallback];
  }
  const sorted = [...samples].sort((a, b) => a - b);
  const lo = percentile(sorted, 0.05);
  const hi = percentile(sorted, 0.95);
  if (!(Number.isFinite(lo) && Number.isFinite(hi)) || hi - lo < MIN_SPAN[name]) {
    issues.push(`${name}: barely moved — kept the default range`);
    return [...fallback];
  }
  return [lo, hi];
}

export function fitRanges(samples: CalibrationSamples): FitResult {
  const issues: string[] = [];
  const aperture = fitAxis('aperture', samples.aperture, issues);
  const spread = fitAxis('spread', samples.spread, issues);
  const height = fitAxis('height', samples.height, issues);

  // Step 4: the resting height raises the height floor so "arms dropped" is
  // genuinely silent for THIS performer. Clamped so the playable span never
  // collapses below its minimum.
  if (samples.rest.length >= 10) {
    const rest = median(samples.rest);
    if (Number.isFinite(rest)) {
      const raised = Math.max(height[0], rest + REST_MARGIN);
      if (raised < height[1] - MIN_SPAN.height) {
        height[0] = raised;
      } else {
        issues.push('rest: resting arms sit inside the playing range — height floor not raised');
      }
    }
  } else {
    issues.push('rest: too few samples — height floor unchanged');
  }

  return { ranges: { aperture, spread, height }, issues };
}

/* ------------------------------- persistence ------------------------------ */

/**
 * THE one localStorage exception in this app (decided 2026-08-09): calibrated
 * ranges are a few device-specific numbers, not content. Everything else
 * remains unpersisted by design. The wizard owns the actual storage calls;
 * these functions own the format, so a corrupt or foreign value can never
 * poison the pipeline — parse returns null, never a partial range.
 */
export const CALIBRATION_KEY = 'gesture-synth.calibration.v1';

export function serializeCalibration(ranges: Ranges): string {
  return JSON.stringify({ v: 1, ranges });
}

const isPair = (x: unknown): x is [number, number] =>
  Array.isArray(x) && x.length === 2 &&
  Number.isFinite(x[0]) && Number.isFinite(x[1]) && (x[0] as number) < (x[1] as number);

export function parseCalibration(json: string | null): Ranges | null {
  if (!json) return null;
  try {
    const doc = JSON.parse(json) as { v?: unknown; ranges?: Partial<Record<keyof Ranges, unknown>> };
    if (doc?.v !== 1 || !doc.ranges) return null;
    const { aperture, spread, height } = doc.ranges;
    if (!isPair(aperture) || !isPair(spread) || !isPair(height)) return null;
    return { aperture: [...aperture], spread: [...spread], height: [...height] };
  } catch {
    return null;
  }
}
